import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab, Button, Typography, Snackbar, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { getCostEstimation, saveCostEstimation, getFinancialModel, updateFinancialModel, getStaffingData, saveStaffingData, createOutcome, updateOutcome } from '../../api/api';
import { buttonStyles, formControlStyles, layoutStyles } from '../../styles/commonStyles';
import { ActionButton, BackButton, StyledFormControl, PageHeader } from '../shared/StyledComponents';
import OutcomeInfoStep from './OutcomeInfoStep';
import FinancialModelStep from './FinancialModelStep';
import CostEstimationWizard from './CostEstimationWizard';
import StaffingStep from './StaffingStep';
import ForecastVsActualsStep from './ForecastVsActualsStep';
import OutcomeRoadmapTab from './OutcomeRoadmapTab';

function OutcomeWizard({ selectedOutcome, onBack, onBackToHome, appName, readOnly = false, initialStep = 0 }) {
  const [step, setStep] = useState(initialStep);
  const [outcomeData, setOutcomeData] = useState(selectedOutcome || {});
  const [costEstimationData, setCostEstimationData] = useState({});
  const [financialModelData, setFinancialModelData] = useState({});
    const [staffingData, setStaffingData] = useState({});
  const latestStaffingFormData = useRef({});
  const savingOutcomeInFlight = useRef(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Listen for plan selection from PlanningPage for staffing step
  useEffect(() => {
    const handlePlanSelectedForStaffing = (event) => {
      // This will be handled by the StaffingStep component directly
      console.log('OutcomeWizard: Plan selected for staffing:', event.detail.planId);
    };

    window.addEventListener('planSelectedForStaffing', handlePlanSelectedForStaffing);
    
    return () => {
      window.removeEventListener('planSelectedForStaffing', handlePlanSelectedForStaffing);
    };
  }, []);

  useEffect(() => {
    const loadExistingData = async () => {
      if (selectedOutcome?.id) {
        try {
          console.log('OutcomeWizard: Loading existing data for outcome:', selectedOutcome.id);
          
          // Load cost estimation
          try {
            const costResponse = await getCostEstimation(selectedOutcome.id);
            const costData = costResponse.data || costResponse || {};
            setCostEstimationData(costData);
            console.log('OutcomeWizard: Loaded existing cost estimation:', costData);
          } catch (error) {
            console.log('OutcomeWizard: No existing cost estimation found');
            setCostEstimationData({});
          }

          // Load financial model
          try {
            const financialResponse = await getFinancialModel(selectedOutcome.id);
            const financialData = financialResponse.data || financialResponse || {};
            console.log('OutcomeWizard: Loaded existing financial model:', financialData);
            
            // Handle legacy data migration from "roles" to "teams"
            if (financialData.roles && !financialData.teams) {
              console.log('OutcomeWizard: Migrating legacy roles to teams');
              financialData.teams = financialData.roles.map(role => ({
                title: role.title || 'Unknown Team',
                count: role.count || 1,
                avg_salary: role.avg_salary || 163000,
                time_percentage: role.time_percentage || 100
              }));
              delete financialData.roles;
            }
            
            setFinancialModelData(financialData);
          } catch (error) {
            console.log('OutcomeWizard: No existing financial model found');
            setFinancialModelData({});
          }

          // Load staffing data
          try {
            const staffingResponse = await getStaffingData(selectedOutcome.id);
            const staffingDataResponse = staffingResponse.data || staffingResponse || {};
            console.log('OutcomeWizard: Loaded existing staffing data:', staffingDataResponse);
            setStaffingData(staffingDataResponse);
          } catch (error) {
            console.log('OutcomeWizard: No existing staffing data found');
            setStaffingData({});
          }
        } catch (error) {
          console.error('OutcomeWizard: Error loading existing data:', error);
        }
      }
    };
    loadExistingData();
  }, [selectedOutcome?.id]);

  const refreshData = async () => {
    if (outcomeData?.id) {
      try {
        console.log('OutcomeWizard: Refreshing data for outcome:', outcomeData.id);
        
        // Refresh cost estimation
        try {
          const costResponse = await getCostEstimation(outcomeData.id);
          const costData = costResponse.data || costResponse || {};
          setCostEstimationData(costData);
          console.log('OutcomeWizard: Refreshed cost estimation data');
        } catch (error) {
          console.log('OutcomeWizard: No cost estimation data to refresh');
          setCostEstimationData({});
        }

        // Refresh financial model
        try {
          const financialResponse = await getFinancialModel(outcomeData.id);
          const financialData = financialResponse.data || financialResponse || {};
          console.log("OutcomeWizard: Refreshed financial model data:", financialData);
          
          // Handle legacy data migration from "roles" to "teams"
          if (financialData.roles && !financialData.teams) {
            console.log('OutcomeWizard: Migrating legacy roles to teams during refresh');
            financialData.teams = financialData.roles.map(role => ({
              title: role.title || 'Unknown Team',
              count: role.count || 1,
              avg_salary: role.avg_salary || 163000,
              time_percentage: role.time_percentage || 100
            }));
            delete financialData.roles;
          }
          
          // Check if the refreshed data has milestones with efficiency data
          if (financialData?.milestones) {
            financialData.milestones.forEach((milestone, index) => {
              console.log(`OutcomeWizard: Refreshed milestone ${index} efficiency_gains:`, milestone.efficiency_gains);
              console.log(`OutcomeWizard: Refreshed milestone ${index} efficiency_types:`, milestone.efficiency_types);
            });
          }
          
          setFinancialModelData(financialData);
        } catch (error) {
          console.log('OutcomeWizard: No financial model data to refresh');
          setFinancialModelData({});
        }

        // Refresh staffing data
        try {
          const staffingResponse = await getStaffingData(outcomeData.id);
          const staffingDataResponse = staffingResponse.data || staffingResponse || {};
          setStaffingData(staffingDataResponse);
          console.log('OutcomeWizard: Refreshed staffing data');
        } catch (error) {
          console.log('OutcomeWizard: No staffing data to refresh');
          setStaffingData({});
        }
      } catch (error) {
        console.error('OutcomeWizard: Error refreshing data:', error);
      }
    }
  };

    // Helper function to save current step data
  const saveCurrentStepData = async () => {
    if (readOnly) return;
    try {
      if (step === 0 && outcomeData && Object.keys(outcomeData).length > 0) {
        console.log('OutcomeWizard: Saving outcome data');
        await handleSaveOutcome(outcomeData);
      }
      
      if (step === 1 && costEstimationData && Object.keys(costEstimationData).length > 0) {
        console.log('OutcomeWizard: Saving cost estimation data');
        await handleSaveCostEstimation(costEstimationData);
      }
      
      if (step === 2 && financialModelData && Object.keys(financialModelData).length > 0) {
        console.log('OutcomeWizard: Saving financial model data');
        await handleSaveFinancialModel(financialModelData);
      }
      
      if (step === 3) {
        const dataToSave = Object.keys(latestStaffingFormData.current).length > 0
          ? latestStaffingFormData.current
          : staffingData;
        if (dataToSave && Object.keys(dataToSave).length > 0) {
          console.log('OutcomeWizard: Saving staffing data');
          await handleSaveStaffingData(dataToSave);
        }
      }
    } catch (error) {
      console.error('OutcomeWizard: Error saving current step data:', error);
      setSnackbar({
        open: true,
        message: `Failed to save changes: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      throw error;
    }
  };

  const handleTabChange = async (event, newValue) => {
    try {
      console.log('OutcomeWizard: Changing from step', step, 'to step', newValue);
      
      // Save current step data before switching
      await saveCurrentStepData();
      
      // Only refresh data when going to step 0 (overview) or when switching TO financial model step
      if (newValue === 0 || newValue === 2) {
        await refreshData();
      }
      
      setStep(newValue);
    } catch (error) {
      console.error('OutcomeWizard: Error during step change:', error);
      // Still allow step change even if save fails
      setStep(newValue);
    }
  };

    // Handle navigation back to outcomes overview
  const handleBackToOutcomes = async () => {
    try {
      console.log('OutcomeWizard: Saving data before navigating back to outcomes overview');
      setSavingOutcome(true);
      await saveCurrentStepData();
      console.log('OutcomeWizard: Data saved, navigating back to outcomes overview');
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('OutcomeWizard: Error saving data before navigation:', error);
      // Still allow navigation even if save fails
      if (onBack) {
        onBack();
      }
    } finally {
      setSavingOutcome(false);
    }
  };

  // Handle navigation back to home
  const handleBackToHome = async () => {
    try {
      console.log('OutcomeWizard: Saving data before navigating back to home');
      setSavingOutcome(true);
      await saveCurrentStepData();
      console.log('OutcomeWizard: Data saved, navigating back to home');
      if (onBackToHome) {
        onBackToHome();
      } else if (onBack) {
        // Fallback to onBack if onBackToHome is not provided
        onBack();
      }
    } catch (error) {
      console.error('OutcomeWizard: Error saving data before navigation:', error);
      // Still allow navigation even if save fails
      if (onBackToHome) {
        onBackToHome();
      } else if (onBack) {
        onBack();
      }
    } finally {
      setSavingOutcome(false);
    }
  };

  // Save outcome data
  const handleSaveOutcome = async (data) => {
    // Prevent concurrent create calls — updates are idempotent so no guard needed there
    if (!data.id && savingOutcomeInFlight.current) return;
    if (!data.id) savingOutcomeInFlight.current = true;
    try {
      console.log('OutcomeWizard: Saving outcome data:', data);
      setSavingOutcome(true);

      let response;
      if (data.id) {
        // Update existing outcome
        response = await updateOutcome(data.id, data);
        console.log('OutcomeWizard: Outcome updated:', response);
      } else {
        // Create new outcome
        response = await createOutcome(data);
        console.log('OutcomeWizard: New outcome created:', response);

        // Update local state with the new ID
        if (response && response.id) {
          setOutcomeData(prev => ({ ...prev, id: response.id }));
        }

        // Dispatch creation event for new outcomes
        window.dispatchEvent(new CustomEvent('outcomeCreated', {
          detail: { outcomeId: response.id, outcomeData: response }
        }));
      }

      // Trigger outcome update event
      window.dispatchEvent(new CustomEvent('outcomeUpdated', {
        detail: { outcomeId: data.id || response?.id, type: 'outcome_info' }
      }));

      return response;
        } catch (error) {
      console.error('OutcomeWizard: Error saving outcome:', error);
      setSnackbar({
        open: true,
        message: `Failed to save outcome: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      throw error;
    } finally {
      savingOutcomeInFlight.current = false;
      setSavingOutcome(false);
    }
  };

  // Save cost estimation data
  const handleSaveCostEstimation = async (data) => {
    try {
      console.log('OutcomeWizard: Saving cost estimation data:', data);
      setSavingOutcome(true);
      
      // Save to backend via API if outcome ID exists
      if (outcomeData.id && saveCostEstimation) {
        const response = await saveCostEstimation(outcomeData.id, data);
        console.log('OutcomeWizard: Cost estimation saved to backend:', response);
      }
      
      // Update local state
      setCostEstimationData(data);
      console.log('OutcomeWizard: Cost estimation state updated');
      
      // Trigger outcome update event
      window.dispatchEvent(new CustomEvent('outcomeUpdated', { 
        detail: { outcomeId: outcomeData.id, type: 'cost_estimation' } 
      }));
      
      return data;
        } catch (error) {
      console.error('OutcomeWizard: Error saving cost estimation:', error);
      setSnackbar({
        open: true,
        message: `Failed to save cost estimation: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      throw error;
    } finally {
      setSavingOutcome(false);
    }
  };

  // Save financial model data
  const handleSaveFinancialModel = async (data) => {
    try {
      setSavingOutcome(true);
      console.log('OutcomeWizard: Saving financial model with data:', JSON.stringify(data, null, 2));
      
      // Check if milestones have efficiency data
      if (data.milestones) {
        data.milestones.forEach((milestone, index) => {
          console.log(`OutcomeWizard: Saving milestone ${index} efficiency_gains:`, milestone.efficiency_gains);
          console.log(`OutcomeWizard: Saving milestone ${index} efficiency_types:`, milestone.efficiency_types);
        });
      }
      
      // Make sure we're calling the API to save the data to the backend
      const response = await updateFinancialModel(outcomeData.id, data);
      console.log('OutcomeWizard: Financial model saved successfully:', response);
      
      // Update the local state with the saved data
      // Make sure we're preserving all the data, especially the summary information
      const updatedData = {
        ...data,
        // If the response contains data, merge it with our local data
        ...(response.data ? response.data : {})
      };
      
      setFinancialModelData(updatedData);
      
      // Trigger outcome update event
      window.dispatchEvent(new CustomEvent('outcomeUpdated', { 
        detail: { outcomeId: outcomeData.id, type: 'financial_model' } 
      }));
      
      // DON'T refresh data after saving to avoid overwriting local changes
      // await refreshData(); // Commented out to prevent data loss
      return response;
        } catch (error) {
      console.error('OutcomeWizard: Error saving financial model:', error);
      setSnackbar({
        open: true,
        message: `Failed to save financial model: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      throw error;
    } finally {
      setSavingOutcome(false);
    }
  };

  // Save staffing data
  const handleSaveStaffingData = async (data) => {
    try {
      setSavingOutcome(true);
      console.log('OutcomeWizard: Saving staffing data:', data);
      
      // Save to backend if API exists
      if (saveStaffingData && outcomeData.id) {
        const response = await saveStaffingData(outcomeData.id, data);
        console.log('OutcomeWizard: Staffing data saved to backend:', response);
      }
      
      // Update local state
      setStaffingData(data);
      
      // Trigger outcome update event
      window.dispatchEvent(new CustomEvent('outcomeUpdated', { 
        detail: { outcomeId: outcomeData.id, type: 'staffing' } 
      }));
      
      // Refresh data after saving
      await refreshData();
      return data;
        } catch (error) {
      console.error('OutcomeWizard: Error saving staffing data:', error);
      setSnackbar({
        open: true,
        message: `Failed to save staffing data: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      throw error;
    } finally {
      setSavingOutcome(false);
    }
  };

  // Updated tab labels to include all steps
  const tabLabels = ['Outcome Info', 'Cost Estimation', 'Financial Model', 'Staffing', 'Forecast vs Actuals', 'Roadmap'];
  
  return (
    <Box>
      {/* Header with back button and title */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        {onBackToHome && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToHome}
            size="small"
            disabled={savingOutcome}
          >
            Home
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToOutcomes}
          size="small"
          disabled={savingOutcome}
        >
          Outcomes
        </Button>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1, ml: 1 }}>
          {appName || (selectedOutcome?.name ? `Edit: ${selectedOutcome.name}` : 'Create New Outcome')}
        </Typography>
      </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={step} onChange={handleTabChange} aria-label="outcome wizard tabs">
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
                    <ActionButton
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={async () => {
              try {
                await saveCurrentStepData();
                setSnackbar({
                  open: true,
                  message: 'Changes saved successfully',
                  severity: 'success'
                });
              } catch (error) {
                // Error handling is already done in saveCurrentStepData
              }
            }}
            disabled={savingOutcome || readOnly}
            size="small"
            sx={{ mr: 2 }}
          >
            {savingOutcome ? 'Saving...' : 'Save'}
          </ActionButton>
        </Box>
      </Box>

      {step === 0 && (
        <OutcomeInfoStep
          data={outcomeData}
          onUpdate={setOutcomeData}
          onSave={handleSaveOutcome}
          loading={savingOutcome}
          costEstimationData={costEstimationData}
          financialModelData={financialModelData}
          staffingData={staffingData}
          setSavingOutcome={setSavingOutcome}
          readOnly={readOnly}
          hideButtons={true}
        />
      )}
      {step === 1 && (
        <CostEstimationWizard
          outcomeId={outcomeData.id}
          data={costEstimationData}
          outcomeStartDate={outcomeData.start_date}
          outcomeName={outcomeData.name}
          onUpdate={handleSaveCostEstimation}
          onSave={handleSaveCostEstimation}
          autoSave={true}
          readOnly={readOnly}
          hideButtons={true}
        />
      )}
      {step === 2 && (
        <FinancialModelStep
          outcomeId={outcomeData.id}
          outcomeName={outcomeData.name}
          data={financialModelData}
          onSave={handleSaveFinancialModel}
          costEstimationData={costEstimationData}
          refreshData={refreshData}
          autoSave={true}
          readOnly={readOnly}
          hideButtons={true}
        />
      )}
      {step === 3 && (
        <StaffingStep
          outcomeId={outcomeData.id}
          outcomeName={outcomeData.name}
          data={staffingData}
          costEstimationData={costEstimationData}
          outcomeData={outcomeData}
          onUpdate={handleSaveStaffingData}
          onFormDataChange={(fd) => { latestStaffingFormData.current = fd; }}
          refreshData={refreshData}
          autoSave={true}
          hideButtons={true}
          readOnly={readOnly}
        />
      )}
      {step === 4 && (
        <ForecastVsActualsStep
          outcomeId={outcomeData.id}
          outcomeName={outcomeData.name}
          hideButtons={true}
          readOnly={readOnly}
        />
      )}
      {step === 5 && (
        <OutcomeRoadmapTab
          outcomeId={outcomeData.id}
          outcomeName={outcomeData.name}
          readOnly={readOnly}
        />
      )}

            <Backdrop open={savingOutcome} sx={{ zIndex: 2000 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2, color: 'white' }}>
            Saving changes...
          </Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OutcomeWizard;